import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { space, color, flexbox, border, layout } from 'styled-system';
import { useSelector, useDispatch } from 'react-redux';
import { getStoresRequest } from 'redux-api/action/stores';
import { getReportStatusesRequest } from 'redux-api/action/reportStatuses';
import selectStores from 'redux-api/reselect/stores';
import selectReportStatuses from 'redux-api/reselect/reportStatuses';
import { themeGet } from '@styled-system/theme-get';
import Card from 'theme/card';
import Text from 'theme/text';
import ItemTypeCard from 'theme/item-type-card';
import OrderTypeCard from 'theme/order-type-card';
import translations from 'translations';
import ToolTip from 'theme/tooltip';
import Loader from 'theme/icons/loader';
import AlertMessage from 'theme/alert-message';

const Wrapper = styled.div`
  ${space}

  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;

  ${flexbox}
  ${layout}
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const WrapLoader = styled.div`
  width: 25px;
  height: 25px;
  animation: ${rotate} 2s linear infinite;
  color: ${themeGet('colors.primary_500')};
`;

const DataCard = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 233px;

  border-radius: 12px;

  padding: 8px 16px;

  ${space}
  ${color}
  ${border}
`;

const StyledText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
`;

const StoreCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 357px;

  border: 1px solid;
  box-sizing: border-box;
  border-radius: 12px;

  ${space}
  ${color}
  ${border}
`;

function DataStoresAnalytics() {
  const dispatch = useDispatch();

  const { languageCode } = useSelector((state) => state.config);
  const { isWaiting } = useSelector((state) => state.reportStatuses);
  const { isWaitingStores } = useSelector((state) => state.stores);
  const cacheKey = 'CACHED';

  const stores = useSelector(selectStores);
  const reportStatuses = useSelector((state) => selectReportStatuses(state, cacheKey));

  useEffect(() => {
    dispatch(getStoresRequest());
    dispatch(getReportStatusesRequest({ key: cacheKey }));
  }, [dispatch]);

  return (
    <Wrapper p="l">
      <Card width="100%" bg="white" border="none" p="l">
        <Flex flexDirection="row" alignItems="center">
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
            {translations[languageCode]['Data availability']}
          </Text>
          <Text ml="xs" fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_600">
            {translations[languageCode].Temporary}
          </Text>
        </Flex>

        <Text my="m" fontSize="pLarge" lineHeight="pLarge" fontWeight="normal" color="grey_600">
          {translations[languageCode].analytics_data_description}
        </Text>

        <Flex flexDirection="row" flexWrap="wrap">
          {isWaiting ? (
            <WrapLoader>
              <Loader width={25} />
            </WrapLoader>
          ) : (
            <>
              {!reportStatuses.length && (
                <Text color="grey_400">
                  ({translations[languageCode].analytics_no_data_description})
                </Text>
              )}
              {reportStatuses.map((report) => (
                <DataCard
                  key={report.key}
                  m="xs"
                  bg="primary_50"
                  border={report.lastDays ? '2px solid' : '1px solid'}
                  borderColor={report.lastDays ? 'primary_400' : 'primary_200'}
                  data-tip
                  data-for={report.key}
                >
                  <StyledText
                    fontSize="pLarge"
                    lineHeight="pLarge"
                    fontWeight="bold"
                    color="grey_600"
                  >
                    {report.key}
                  </StyledText>

                  <ToolTip
                    forData={report.key}
                    text={report.key}
                    arrowColor="#098EA0"
                    backgroundColor="#098EA0"
                    place="top"
                    effect="solid"
                  />

                  <Text
                    style={{ whiteSpace: 'nowrap' }}
                    fontSize="pSmall"
                    lineHeight="pSmall"
                    fontWeight={report.lastDays ? 'bold' : 'medium'}
                    color={report.lastDays ? 'primary_500' : 'grey_600'}
                  >
                    {report.date}
                  </Text>
                </DataCard>
              ))}
            </>
          )}
        </Flex>
      </Card>

      <Card width="100%" bg="white" border="none" p="l" my="s">
        <Flex flexDirection="row" alignItems="center">
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
            {translations[languageCode]['Store availability']}
          </Text>
          <Text ml="xs" fontSize="h3" lineHeight="h3" fontWeight="medium">
            {translations[languageCode].Temporary}
          </Text>
        </Flex>

        <Row>
          <Card width="100%" borderColor="orange_400" bg="accent_light" p="m" my="s">
            <AlertMessage
              message={translations[languageCode]['Fastbook Alert']}
            />
          </Card>
        </Row>

        <Text my="m" fontSize="pLarge" lineHeight="pLarge" fontWeight="normal" color="grey_600">
          {translations[languageCode].analytics_stores_description}
        </Text>

        <Flex flexDirection="row" flexWrap="wrap">
          {isWaitingStores ? (
            <WrapLoader>
              <Loader width={25} />
            </WrapLoader>
          ) : (
            <>
              {stores.map((store) => (
                <StoreCard key={store.key} m="xs" bg="primary_50" p="s" borderColor="primary_200">
                  <StyledText fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
                    {store.name}
                  </StyledText>

                  <Flex flexDirection="column">
                    <StyledText
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="medium"
                      color="grey_400"
                      mt="xs"
                    >
                      {translations[languageCode].analytics_stores_itemTypes_label}
                    </StyledText>
                    <Flex flexDirection="row">
                      {store.itemTypes.map((itemType) => (
                        <ItemTypeCard key={itemType.key} type={itemType.key} style={{ mr: 'xs' }} />
                      ))}
                    </Flex>
                  </Flex>

                  <Flex flexDirection="column">
                    <StyledText
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="medium"
                      color="grey_400"
                      mt="xs"
                    >
                      {translations[languageCode].analytics_stores_orderTypes_label}
                    </StyledText>
                    <Flex flexDirection="row">
                      {store.orderTypes.map((orderType) => (
                        <OrderTypeCard
                          key={orderType.key}
                          type={orderType.key}
                          style={{ mr: 'xs' }}
                        />
                      ))}
                    </Flex>
                  </Flex>
                </StoreCard>
              ))}
            </>
          )}
        </Flex>
      </Card>
    </Wrapper>
  );
}

export default DataStoresAnalytics;
